import { DateTime } from "luxon";
import { fenceValueWithVat, otherValueWithVat } from "@/utils";
import { pdf_contract, CONTRACT_FORM_CODE } from "@/documents/contract/pdf_contract.js";
import { moduleSizeText, moduleHeightText, moduleNameKey, isPost } from "@/utils/fenceModules";

export default {
  create(contract, executantDetails, firmaMontajContract, aditionalExtra) {
    return {
      contract,
      executantDetails,
      firmaMontajContract,
      aditionalExtra,

      // The offer this act replaces: the contract's own for act 1, the one the previous act
      // brought after it. Act 1 keeps reading the contract, as it did when a contract had one act.
      get previousOffer() {
        return this.aditionalExtra.previous || this.contract
      },

      get previousOfferId() {
        const previous = this.aditionalExtra.previous
        return previous ? previous.new_offer_id : this.contract.oferta_id
      },

      get previousOfferDate() {
        const previous = this.aditionalExtra.previous
        return DateTime.fromISO(String(previous ? previous.date_new_offer : this.contract.oferta_data)).toFormat("dd.MM.yyyy")
      },

      // Where the replaced offer comes from, as said after its price in Art.2
      get previousOfferOrigin() {
        const previous = this.aditionalExtra.previous
        if (!previous) return "amely alapján a szerződés készült"
        const date = DateTime.fromISO(String(previous.data)).toFormat("dd.MM.yyyy")
        return `amelyet a ${previous.id_aditional} számú, ${date} dátumú szerződésmódosítás vezetett be`
      },
      
      get language() {
        return 'de-DE'
      },

      get isMontajSeparat() {
        return this.contract.detalii.montaj_separat
      },

      get montajFirma1Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI DESIGN S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022003676350, cod fiscal 46619944 (neplatitor de TVA), cont bancar: RO74BTRLRONCRT0CH8981101, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get montajFirma2Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI PARTENER S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022005419357, cod fiscal 47219300 (neplatitor de TVA), cont bancar: RO14BTRLRONCRT0664679101, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get montajFirma3Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI PROIECTE S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022004612351, cod fiscal 46926167 (neplatitor de TVA), cont bancar: RO11BTRLRONCRT0661673901, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get actClient() {
        if (this.contract.detalii.alt_document) {
          return this.contract.detalii.alt_document + ' Nr. ' + this.contract.detalii.alt_document_nr
        }
        return `C.I. serie ${this.contract.detalii.serie_ci} nr. ${this.contract.detalii.numar_ci}`
      },

      get adresaClient() {
        if (this.contract.persoana) {
          return `székhelye: ${this.contract.adresa}`
        } else {
          return `lakcíme: ${this.contract.detalii.adresa}`
        }
      },

      get exemplareContract() {
        return this.isMontajSeparat ? "három (3)" : "két (2)"
      },
      
      get moduleGard() {
        return this.aditionalExtra.gard_obj.map(modul => ({
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              // a post has no width, so it is quoted by height alone
              text: isPost(modul.tip)
                ? `${this.denumireModulGard(modul)} - M ${moduleHeightText(modul)} mm - ${modul.buc} darab`
                : `${this.denumireModulGard(modul)} - méretek H x M ${moduleSizeText(modul)} mm - ${modul.buc} darab`
            }]
          }]
        }))
      },

      get moduleCustom() {
        if (!this.aditionalExtra.alte_obj) return []
        return this.aditionalExtra.alte_obj.map(modul => ({
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              text: `${this.getTranslatedName(modul.denumire)} - ${modul.cantitate} darab`
            }]
          }]
        }))
      },

      get executantFirmaText() {
        return `1. CUPROLLI MANUFACTURING SRL [CUPROLLI MANUFACTURING Kft. Románia], székhelye CAPRIOAREI utca 11, GHIRODA, Timis megye, Irányítószám 307200, Románia, romániai cégjegyzékszáma J2016000468359, romániai adószáma RO 35671874, IBAN: RO89 BTRL EURC RT03 3757 8301, BIC: BTRLRO22, Bank néve: Banca Transilvania a továbbiakban iSTRUCT, képviselője Octavian Rosu, mint Ügyvezető.`
      },

      // What a module is called in this contract, keyed by type and variant. Adding a type is a
      // key here and a line in moduleNameKey - never another branch below.
      moduleNames: {
        panel: "Panel",
        pedestrianGate: "Gyalogsági kapu",
        pedestrianGateSidePanel: "Gyalogsági kapu oldalpanellel",
        swingGateSingleLeaf: "Egyszárnyú lengőkapu",
        swingGateDoubleLeaf: "Kétszárnyú lengőkapu",
        swingGateBiFoldSingle: "Egyszárnyú bi-fold lengőkapu",
        swingGateBiFoldDouble: "Kétszárnyú bi-fold lengőkapu",
        slidingGateSingleLeaf: "Egyszárnyú tolókapu",
        slidingGateDoubleLeaf: "Kétszárnyú tolókapu",
        slidingGateTelescopic: "Teleszkópikus tolókapu",
        slidingGateArticulated: "Csuklós tolókapu",
        selfSupportingGate: "Önhordó kapu",
        posts: "Oszlopok",
        post80: "Oszlop 80 × 80 mm",
        post100: "Oszlop 100 × 100 mm",
        post200: "Oszlop 200 × 100 mm",
        framedPanel: "Kerítés keretes panel"
      },

      denumireModulGard(modul) {
        const key = moduleNameKey(modul.tip, modul.tip_poarta_mare, modul.tip_stalp)
        // a type with no key only ever comes from an offer saved with something since retired
        let name = (key && this.moduleNames[key]) || String(modul.tip || "").replace(/_/g, " ")
        if (modul.pp_included) name += " gyalogos kapuval egybeépítve"
        return name
      },

      getTranslatedName(name) {
        let newName = name
        if (name == 'Kit automatizare premium poarta culisanta - BFT Deimos Ultra') {
            newName = 'Prémium tolókapu automatizálási készlet - BFT Deimos Ultra'
        }
        if (name == 'Kit automatizare premium poarta culisanta - BFT Ares Veloce') {
            newName = 'Prémium tolókapu automatizálási készlet - BFT Ares Veloce'
        }
        if (name == 'Kit automatizare premium poarta batanta - BFT Kustos Ultra A40') {
            newName = 'Prémium kétszárnyú kapu automatizálási készlet - BFT Kustos Ultra A40'
        }
        if (name == 'Kit automatizare premium poarta batanta - BFT Virgo') {
            newName = 'Prémium kétszárnyú kapu automatizálási készlet - BFT Virgo'
        }
        return newName
      },

      formatParagraph(textContent, alignment, textType) {
        return {
          type: "paragraph",
          attrs: { textAlign: alignment },
          content: [{
            type: "text",
            text: textContent,
            ...(textType && { marks: [{ type: textType }] })
          }]
        }
      },

      // Main document structure builder
      buildDocumentStructure() {
        return {
          type: "doc",
          content: [
            // Header
            {
              type: "heading",
              attrs: { textAlign: "left", level: 1 },
              content: [{ type: "text", text: `SZERZŐDÉSMÓDOSÍTÁS Nr. ${this.aditionalExtra.id_aditional} / ${DateTime.fromISO(String(this.aditionalExtra.data)).toFormat("dd.MM.yyyy")}` }]
            },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 3 },
              content: [{type: "text", text: `a. ${this.contract.id_contract} / ${DateTime.fromISO(String(this.contract.data)).toFormat("dd.MM.yyyy")}. számú VÁLLALKOZÁSI SZERZŐDÉSHEZ`}]
            },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
            },
             {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "A SZERZŐDŐ FELEK" }]
            },
            this.formatParagraph(this.executantFirmaText, "left"),
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `2. ${this.contract.nume.toUpperCase()}, ${this.adresaClient}, személyi ${this.actClient} (ha esedékes), a továbbiakban mint Megrendelő.`
              }]
            },
            { type: "blockquote"},
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: {
                textAlign: "left",
                level: 3,
              },
              content: [
                {
                  type: "text",
                  text: `E jelen szerződésmódosítással a ${this.contract.id_contract} / ${DateTime.fromISO(String(this.contract.data)).toFormat("dd.MM.yyyy")}. számú vállalkozási szerződés az alábbi pontokban módosul:`,
                },
              ],
            },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
              content: [
                {
                  type: "text",
                  text:
                    "1. A Megrendelő befejezte azokat a munkarészeket, amelyek az ő felelősségi körébe tartoznak, így a Vállalkozó elvégezheti a végső felméréseket.",
                },
              ]
            },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
              content: [
                {
                  type: "text",
                  text: `2. A ${this.previousOfferId} számú ${this.previousOfferDate} dátumú ajánlat, valamint a ${(fenceValueWithVat(this.previousOffer) + otherValueWithVat(this.previousOffer)).toFixed(2)} euró összegű ár, ${this.previousOfferOrigin}, a ${this.aditionalExtra.new_offer_id} számú ${DateTime.fromISO(String(this.aditionalExtra.date_new_offer)).toFormat("dd.MM.yyyy")} dátumú ajánlattal és a ${(fenceValueWithVat(this.aditionalExtra) + otherValueWithVat(this.aditionalExtra)).toFixed(2)} euró összeggel kerül felváltásra. Ez az új ajánlat a szerződés elválaszthatatlan részét képezi, és az alábbi elemekre és méretekre épül, amelyek a ....................... dátumú végső felmérés alapján kerültek meghatározásra.`,
                },
              ]
            },
            {
              type: "bulletList",
              content: this.moduleGard,
            },
            {
              type: "bulletList",
              content: this.moduleCustom,
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
              content: [
                {
                  type: "text",
                  text: `Jelen szerződésmódosítás ${DateTime.fromISO(String(this.aditionalExtra.data)).toFormat("dd.MM.yyyy")}. napján készült, ${this.exemplareContract} azonos példányban, amelyekből mindkét fél egy-egy példányt kapott.`,
                },
              ]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            // Signatures
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                marks: [{ type: "bold" }],
                text: "VÁLLALKOZÓ"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: 'CUPROLLI MANUFACTURING SRL (iSTRUCT)' }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: this.executantDetails.legal_representative }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{
                type: "text",
                marks: [{ type: "bold" }],
                text: "MEGRENDELŐ"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: this.contract.nume.toUpperCase() }]
            },
            this.contract.persoana ? {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: this.contract.persoana }]
            } : { type: "blockquote" }
          ]
        }
      },

      // PDF Generation
      async generatePdf(documentText) {
        const doc = await pdf_contract.createContractPDF(documentText.content, contract.country, CONTRACT_FORM_CODE)
        return doc
      }
    }
  }
}